import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useState } from "react";
import Lenis from "lenis";
import api from "@/lib/api";
import { FALLBACK_CONTENT, scrollToSection } from "@/lib/content";
import { useLocation } from "react-router-dom";
import { useT } from "@/i18n";
import { LogIn } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Background from "@/components/landing/Background";
import ChatWidget from "@/components/landing/ChatWidget";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import About from "@/components/landing/About";
import Knowledge from "@/components/landing/Knowledge";
import Faq from "@/components/landing/Faq";
import Services from "@/components/landing/Services";
import Pricing from "@/components/landing/Pricing";
import Testimonials from "@/components/landing/Testimonials";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import CookieBanner from "@/components/landing/CookieBanner";
import AnnouncementPopup from "@/components/landing/AnnouncementPopup";

export default function Landing() {
  const { t } = useT();
  useSeo({ title: t.meta_title, description: "Wirtualne biuro Warszawa Mokotów (ul. Domaniewska 17/19) — adres do rejestracji firmy, odbiór i skanowanie korespondencji, sala konferencyjna i księgowość. Pakiety od 37,91 zł netto/mc.", path: "/" });
  const [content, setContent] = useState(FALLBACK_CONTENT);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) setTimeout(() => scrollToSection(hash), 400);
  }, [hash]);


  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    api
      .get("/content")
      .then((r) => setContent((prev) => ({ ...prev, ...r.data })))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen text-foreground overflow-x-clip">
      <Background />
      <Navbar announcement={content.announcement} />
      <main>
        <Hero content={content} />
        <Marquee />
        <About />
        <Services />
        <Pricing packages={content.packages} promo={content.pricing_promo} show24={!!content.billing_24_enabled} note24={content.billing_24_note || ""} comparison={content.comparison} />
        {content.show_testimonials !== false && <Testimonials items={content.testimonials} />}
        <Knowledge />
        <Faq />
        <Contact content={content} />
      </main>
      <Footer content={content} />
      <CookieBanner />
      <AnnouncementPopup popup={content.popup} />
      <ChatWidget content={content} />
      <a
        data-testid="floating-panel-tab"
        href="https://panel.twojebiuro.waw.pl/auth/login"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t.floating.panel}
        className="group fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-l-xl shadow-xl px-2.5 py-5 hover:px-4 transition-all duration-300"
      >
        <LogIn size={15} className="shrink-0 transition-transform duration-300 group-hover:translate-x-0.5" />
        <span className="[writing-mode:vertical-rl] rotate-180 font-mono text-[10px] uppercase tracking-[0.25em] font-semibold">
          {t.floating.panel}
        </span>
      </a>
    </div>
  );
}
